// Lightweight Arabic normalization and heuristic stemming for client-side diff

const DIACRITICS = /[\u064B-\u065F\u0670\u06D6-\u06ED]/g // tanwin, tashkeel, superscripts
const TATWEEL = /\u0640/g

export function removeDiacritics(s: string): string {
  return s.normalize('NFC').replace(DIACRITICS, '')
}

export function normalizeLetters(s: string): string {
  return s
    .replace(/[\u0622\u0623\u0625\u0671]/g, '\u0627') // آأإٱ -> ا
    .replace(/\u0629/g, '\u0647') // ة -> ه (light normalization for stems)
    .replace(/\u0649/g, '\u064A') // ى -> ي
    .replace(/[\u0624\u0626]/g, '\u0621') // ؤئ -> ء
}

export function normalizeToken(s: string, opts?: { ignoreDiacritics?: boolean }): string {
  const ignore = opts?.ignoreDiacritics !== false // default true
  const base = ignore ? removeDiacritics(s) : s
  return normalizeLetters(base.replace(TATWEEL, ''))
}

const PREFIX_CHARS = new Set(['\u0648', '\u0641', '\u0628', '\u0643', '\u0644', '\u0633']) // و ف ب ك ل س

export function stripPrefixes(token: string): string {
  let t = token
  let count = 0
  while (count < 3 && t.length > 0 && PREFIX_CHARS.has(t[0])) {
    t = t.slice(1)
    count++
  }
  if (t.startsWith('\u0627\u0644')) { // ال
    t = t.slice(2)
  }
  return t
}

const SUFFIXES = [
  '\u0647\u0645\u0627', // هما
  '\u0643\u0645\u0627', // كما
  '\u0647\u0646',       // هن
  '\u0647\u0645',       // هم
  '\u0643\u0645',       // كم
  '\u0643\u0646',       // كن
  '\u0647\u0627',       // ها
  '\u0646\u0627',       // نا
  '\u0643',             // ك
  '\u064A',             // ي
  '\u0647'              // ه
]

const STEM_ENDINGS = [
  '\u0627\u062A', // ات
  '\u0648\u0646', // ون
  '\u064A\u0646', // ين
  '\u0627\u0646', // ان
  '\u0629',       // ة
  '\u0647',       // ه
  '\u064A',       // ي
  '\u062A'        // ت
]

export function stripSuffixes(token: string): string {
  let t = token
  // longest-first
  for (const suf of SUFFIXES) {
    if (t.endsWith(suf) && t.length - suf.length >= 2) {
      t = t.slice(0, -suf.length)
      break
    }
  }
  return t
}

export function lightStem(token: string): string {
  let t = stripPrefixes(token)
  t = stripSuffixes(t)
  for (const end of STEM_ENDINGS) {
    if (t.endsWith(end) && t.length - end.length >= 3) {
      t = t.slice(0, -end.length)
      break
    }
  }
  return t
}

export function getStem(token: string, opts?: { ignoreDiacritics?: boolean }): string {
  const norm = normalizeToken(token, opts)
  const stem = lightStem(norm)
  return stem
}

export function isMorphNearMatch(a: string, b: string, opts?: { ignoreDiacritics?: boolean }): boolean {
  if (!a || !b) return false
  const na = normalizeToken(a, opts)
  const nb = normalizeToken(b, opts)
  if (na === nb) return true
  const sa = getStem(a, opts)
  const sb = getStem(b, opts)
  if (sa && sb && sa === sb) return true
  return false
}
